/**
 * **Todoist-API** (v1) — nur, was der Import braucht.
 *
 * Aufrufe gehen direkt aus der App an Todoist, nicht über unseren Server; der
 * persönliche Schlüssel bleibt beim Aufrufer im Speicher und wird nie
 * gesichert (docs/concept/todoist-import.md).
 */
import type { z } from 'zod'
import {
  CompletedItemZ,
  TodoistItemZ,
  TodoistProjectZ,
  TodoistSectionZ,
  completedWindows,
  parseRows,
  parseSnapshot,
  type CompletedWindow,
  type TodoistItem,
  type TodoistSection,
  type TodoistSnapshot,
} from './types'

export const TODOIST_API_BASE = 'https://api.todoist.com/api/v1'

/** Wo der Nutzer seinen Schlüssel findet. */
export const TODOIST_TOKEN_SETTINGS_URL = 'https://app.todoist.com/app/settings/integrations/developer'

/** Ab hier sucht der Verlauf, wenn Todoist kein Anmeldedatum nennt: Todoists Start. */
export const TODOIST_EPOCH = new Date('2007-01-01T00:00:00Z')

/** Seitengröße je Abruf. Die Doku nennt kein Maximum; 50 ist Todoists Vorgabe, 200 geht (geprüft 26.09.2026). */
export const PAGE_LIMIT = 200

export interface TodoistUser {
  fullName: string
  email: string
  /** Seit wann es das Konto gibt — dort beginnt der Verlauf der erledigten Aufgaben. */
  joinedAt: string | null
}

/** `aborted` heißt: Der Nutzer hat weitergetippt oder das Fenster geschlossen — nie anzeigen. */
export type TodoistFailure = 'invalid' | 'rateLimited' | 'offline' | 'unknown' | 'aborted'

export type VerifyResult = { ok: true; user: TodoistUser } | { ok: false; reason: TodoistFailure }

/** `notFound`: 404 — den Eintrag gibt es nicht (mehr); das wird durch Wiederholen nicht besser. */
type RequestResult = { ok: true; body: unknown } | { ok: false; reason: TodoistFailure; retryAfterMs?: number; notFound?: boolean }

export interface RequestDeps {
  fetchImpl?: typeof fetch
  /** Pause zwischen zwei Versuchen; im Test ersetzt, damit nichts wirklich wartet. */
  sleep?: (ms: number) => Promise<void>
}

const realSleep = (ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms))

/**
 * Ein Aufruf mit Schlüssel. Wirft nie: Jeder Fehler wird zu einem Grund, den
 * die Seite dem Nutzer nennen kann.
 */
async function todoistRequest(
  path: string,
  token: string,
  init: RequestInit,
  fetchImpl: typeof fetch,
): Promise<RequestResult> {
  const trimmed = token.trim()
  if (!trimmed) return { ok: false, reason: 'invalid' }

  let response: Response
  try {
    response = await fetchImpl(`${TODOIST_API_BASE}${path}`, {
      ...init,
      headers: { ...init.headers, Authorization: `Bearer ${trimmed}` },
    })
  } catch {
    return { ok: false, reason: init.signal?.aborted ? 'aborted' : 'offline' }
  }

  if (response.status === 401 || response.status === 403) return { ok: false, reason: 'invalid' }
  if (response.status === 429) {
    const seconds = Number(response.headers.get('Retry-After'))
    return { ok: false, reason: 'rateLimited', retryAfterMs: Number.isFinite(seconds) && seconds > 0 ? seconds * 1000 : undefined }
  }
  if (response.status === 404) return { ok: false, reason: 'unknown', notFound: true }
  if (!response.ok) return { ok: false, reason: 'unknown' }

  try {
    return { ok: true, body: await response.json() }
  } catch {
    return { ok: false, reason: 'unknown' }
  }
}

/** Wie oft ein Aussetzer wiederholt wird, bevor der Nutzer ihn sieht. */
export const RETRY_DELAYS_MS = [2000, 4000, 8000] as const
const MAX_RETRY_AFTER_MS = 60_000

/**
 * Fängt Aussetzer ab (Netz weg, Todoist bremst, Serverfehler): bis zu drei
 * Wiederholungen mit wachsender Pause; bei 429 so lange, wie Todoist sagt.
 * Ein falscher Schlüssel wird nicht besser, wenn man es noch einmal versucht.
 */
async function withRetry(run: () => Promise<RequestResult>, sleep: (ms: number) => Promise<void>, signal?: AbortSignal): Promise<RequestResult> {
  for (let attempt = 0; ; attempt++) {
    const result = await run()
    if (result.ok || result.reason === 'invalid' || result.reason === 'aborted' || result.notFound) return result
    if (attempt >= RETRY_DELAYS_MS.length) return result
    const delay = result.reason === 'rateLimited' && result.retryAfterMs !== undefined
      ? Math.min(result.retryAfterMs, MAX_RETRY_AFTER_MS)
      : RETRY_DELAYS_MS[attempt]!
    await sleep(delay)
    if (signal?.aborted) return { ok: false, reason: 'aborted' }
  }
}

/** Prüft den Schlüssel mit einem Abruf des Kontos. Wirft nie; ohne Wiederholung — der Nutzer wartet davor. */
export async function verifyTodoistToken(token: string, fetchImpl: typeof fetch = fetch): Promise<VerifyResult> {
  const result = await todoistRequest('/user', token, {}, fetchImpl)
  if (!result.ok) return { ok: false, reason: result.reason }
  const body = (result.body ?? {}) as { full_name?: unknown; email?: unknown; joined_at?: unknown }
  return {
    ok: true,
    user: {
      fullName: typeof body.full_name === 'string' ? body.full_name : '',
      email: typeof body.email === 'string' ? body.email : '',
      joinedAt: typeof body.joined_at === 'string' ? body.joined_at : null,
    },
  }
}

/**
 * Was der volle Sync holt. Filter und Mitarbeitende kommen mit ihren eigenen Tickets. `user` nur für
 * die eigene ID: Erinnerungen anderer Personen in geteilten Projekten bleiben in Todoist.
 */
export const SNAPSHOT_RESOURCE_TYPES = [
  'projects', 'sections', 'items', 'labels', 'notes', 'project_notes', 'reminders', 'reminders_location', 'user',
] as const

export type SnapshotResult = { ok: true; snapshot: TodoistSnapshot } | { ok: false; reason: TodoistFailure }

/**
 * **Alles Aktive in einem Abruf** — ein voller Sync (`sync_token=*`). Todoist
 * erlaubt 100 davon je 15 Minuten; ein Import braucht einen. Wirft nie.
 */
export async function fetchTodoistSnapshot(token: string, deps: RequestDeps = {}): Promise<SnapshotResult> {
  const { fetchImpl = fetch, sleep = realSleep } = deps
  const body = new URLSearchParams({
    sync_token: '*',
    resource_types: JSON.stringify(SNAPSHOT_RESOURCE_TYPES),
  })
  const result = await withRetry(() => todoistRequest('/sync', token, { method: 'POST', body }, fetchImpl), sleep)
  if (!result.ok) return { ok: false, reason: result.reason }
  const snapshot = parseSnapshot(result.body)

  // Der volle Sync kennt archivierte Projekte nicht (gefunden 26.09.2026, #202).
  const archived = await fetchAllPages('/projects/archived', token, TodoistProjectZ, fetchImpl, sleep)
  if (!archived.ok) return archived
  snapshot.skipped += archived.skipped
  for (const project of archived.rows) {
    if (snapshot.projects.some(p => p.id === project.id)) continue
    snapshot.projects.push(project)
    const query = `project_id=${encodeURIComponent(project.id)}`
    const sections = await fetchAllPages(`/sections?${query}`, token, TodoistSectionZ, fetchImpl, sleep)
    if (!sections.ok) return sections
    const items = await fetchAllPages(`/tasks?${query}`, token, TodoistItemZ, fetchImpl, sleep)
    if (!items.ok) return items
    snapshot.sections.push(...sections.rows.filter(s => !snapshot.sections.some(known => known.id === s.id)))
    snapshot.items.push(...items.rows.filter(i => !snapshot.items.some(known => known.id === i.id)))
    snapshot.skipped += sections.skipped + items.skipped
  }
  return { ok: true, snapshot }
}

/** Eine REST-Liste über alle Seiten (`next_cursor`), Zeile für Zeile geprüft. */
async function fetchAllPages<T extends { is_deleted: boolean }>(
  path: string,
  token: string,
  schema: z.ZodType<T>,
  fetchImpl: typeof fetch,
  sleep: (ms: number) => Promise<void>,
): Promise<{ ok: true; rows: T[]; skipped: number } | { ok: false; reason: TodoistFailure }> {
  const rows: T[] = []
  let skipped = 0
  let cursor: string | null = null
  do {
    const params = new URLSearchParams({ limit: String(PAGE_LIMIT) })
    if (cursor) params.set('cursor', cursor)
    const separator = path.includes('?') ? '&' : '?'
    const result = await withRetry(() => todoistRequest(`${path}${separator}${params}`, token, {}, fetchImpl), sleep)
    if (!result.ok) return { ok: false, reason: result.reason }
    const body = (result.body ?? {}) as { results?: unknown; next_cursor?: unknown }
    const page = parseRows(schema, body.results)
    rows.push(...page.rows)
    skipped += page.skipped
    cursor = typeof body.next_cursor === 'string' && body.next_cursor ? body.next_cursor : null
  } while (cursor)
  return { ok: true, rows, skipped }
}

/**
 * **Wie weit der Verlauf schon geholt ist** — der Stand, an dem ein
 * gescheiterter Abruf weitermacht. Lebt nur im Speicher, wie der Schlüssel.
 */
export interface CompletedProgress {
  items: TodoistItem[]
  skipped: number
  /** Noch offene Fenster, das aktuelle zuerst. */
  windows: CompletedWindow[]
  /** Nächste Seite im aktuellen Fenster; `null` = von vorn. */
  cursor: string | null
}

export type CompletedResult =
  | { ok: true; items: TodoistItem[]; skipped: number }
  | { ok: false; reason: TodoistFailure; progress: CompletedProgress }

export interface CompletedOptions {
  /** Beginn des Verlaufs (`joinedAt`); ohne: {@link TODOIST_EPOCH}. */
  since?: Date | null
  until?: Date
  /** Stand eines gescheiterten Versuchs — dort geht es weiter. */
  resume?: CompletedProgress
  signal?: AbortSignal
  /** Je Fenster: bis wohin zurück gerade geholt wird. */
  onProgress?: (reachedBack: Date) => void
}

/**
 * **Alle erledigten Aufgaben** — Todoist gibt sie nur in Fenstern von
 * höchstens drei Monaten heraus, seitenweise. Das Neueste zuerst, eins nach
 * dem anderen. Scheitert ein Fenster auch nach den Wiederholungen, kommt der
 * Stand zurück; mit ihm als `resume` geht es genau dort weiter. Wirft nie.
 */
export async function fetchTodoistCompleted(
  token: string,
  options: CompletedOptions = {},
  deps: RequestDeps = {},
): Promise<CompletedResult> {
  const { fetchImpl = fetch, sleep = realSleep } = deps
  const { signal, onProgress } = options
  const progress: CompletedProgress = options.resume
    ? { ...options.resume, items: [...options.resume.items], windows: [...options.resume.windows] }
    : { items: [], skipped: 0, windows: completedWindows(options.since ?? TODOIST_EPOCH, options.until ?? new Date()), cursor: null }
  const seen = new Set(progress.items.map(item => item.id))
  const stand = (): CompletedProgress => ({ ...progress, items: [...progress.items], windows: [...progress.windows] })

  while (progress.windows.length > 0) {
    const window = progress.windows[0]!
    onProgress?.(window.since)
    if (signal?.aborted) return { ok: false, reason: 'aborted', progress: stand() }

    const params = new URLSearchParams({
      since: window.since.toISOString(),
      until: window.until.toISOString(),
      limit: String(PAGE_LIMIT),
    })
    if (progress.cursor) params.set('cursor', progress.cursor)
    const result = await withRetry(
      () => todoistRequest(`/tasks/completed/by_completion_date?${params}`, token, { signal }, fetchImpl),
      sleep,
      signal,
    )
    if (!result.ok) return { ok: false, reason: result.reason, progress: stand() }

    const body = (result.body ?? {}) as { items?: unknown; next_cursor?: unknown }
    const { rows, skipped } = parseRows(CompletedItemZ, body.items)
    for (const row of rows) {
      if (seen.has(row.id)) continue
      seen.add(row.id)
      progress.items.push(row)
    }
    progress.skipped += skipped

    const next = typeof body.next_cursor === 'string' && body.next_cursor ? body.next_cursor : null
    if (next) {
      progress.cursor = next
    } else {
      progress.windows.shift()
      progress.cursor = null
    }
  }
  return { ok: true, items: progress.items, skipped: progress.skipped }
}

export type SectionsResult =
  | { ok: true; sections: TodoistSection[]; skipped: number }
  | { ok: false; reason: TodoistFailure }

/**
 * **Abschnitte einzeln** (`GET /sections/{id}`, #193) — für archivierte Abschnitte: Weder der volle
 * Sync noch `/sections?project_id=` kennt sie, erledigte Aufgaben verweisen aber auf sie. Eine ID,
 * die es nicht mehr gibt (404, gelöscht), wird übersprungen; jeder andere Fehler lässt den ganzen
 * Abruf scheitern — dann fehlt nichts halb. Wirft nie.
 */
export async function fetchTodoistSections(
  token: string,
  ids: readonly string[],
  deps: RequestDeps = {},
  signal?: AbortSignal,
): Promise<SectionsResult> {
  const { fetchImpl = fetch, sleep = realSleep } = deps
  const sections: TodoistSection[] = []
  let skipped = 0
  for (const id of ids) {
    if (signal?.aborted) return { ok: false, reason: 'aborted' }
    const result = await withRetry(
      () => todoistRequest(`/sections/${encodeURIComponent(id)}`, token, { signal }, fetchImpl),
      sleep,
      signal,
    )
    if (!result.ok) {
      if (result.notFound) continue
      return { ok: false, reason: result.reason }
    }
    const parsed = TodoistSectionZ.safeParse(result.body)
    if (!parsed.success) skipped++
    else if (!parsed.data.is_deleted) sections.push(parsed.data)
  }
  return { ok: true, sections, skipped }
}
